import React, { useEffect, useState } from "react";
import {
  Calendar,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import api from "../api/api";

const NewsBlog = () => {
  const [page, setPage] = useState(0);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const perPage = 3;

  const totalPages = Math.ceil(posts.length / perPage);

  const items = posts.slice(
    page * perPage,
    page * perPage + perPage
  );

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const res = await api.get("/posts?status=published&limit=12");
        setPosts(res.data.items || []);
      } catch (error) {
        console.error("Failed to fetch posts:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  if (loading) {
    return (
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-slate-500 font-semibold">
            Loading latest news...
          </p>
        </div>
      </section>
    );
  }

  if (!posts.length) {
    return null;
  }

  return (
    <section className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-[#ff6b00]" />
            <span className="w-2 h-2 rounded-full bg-[#ff6b00]" />
            <span className="w-2 h-2 rounded-full bg-[#ff6b00]" />

            <span className="ml-2 text-[#ff6b00] font-semibold tracking-widest text-sm uppercase">
              Our Blog
            </span>
          </div>

          <h2 className="text-3xl md:text-5xl font-black text-slate-900 leading-tight">
            Latest{" "}
            <span className="text-green-600">
              News and Blog
            </span>
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
          {items.map((post) => (
            <article
              key={post.id}
              className="bg-white shadow-sm hover:shadow-xl transition-all duration-300 group overflow-hidden"
            >
              <div className="relative overflow-hidden">
                <img
                  src={
                    post.featured_image ||
                    "/assets/images/children.webp"
                  }
                  alt={post.title}
                  className="w-full h-56 object-cover group-hover:scale-110 transition-transform duration-700"
                />

                <div className="absolute bottom-0 left-0 right-0 bg-green-600 text-white py-3 px-5 group-hover:bg-orange-500 transition-colors">
                  <h3 className="font-bold truncate">
                    {post.title}
                  </h3>
                </div>
              </div>

              <div className="p-6">
                <div className="flex items-center gap-2 text-slate-500 text-sm mb-3">
                  <Calendar size={14} />
                  <span>
                    {post.published_at
                      ? post.published_at.slice(0, 10)
                      : post.created_at?.slice(0, 10)}
                  </span>
                </div>

                <p className="text-slate-600 leading-relaxed mb-5 line-clamp-3">
                  {post.excerpt}
                </p>

                <Link
                  to={`/blog/${post.slug}`}
                  className="inline-flex items-center gap-2 text-green-700 font-bold hover:gap-3 hover:text-orange-500 transition-all"
                >
                  READ MORE <ArrowRight size={16} />
                </Link>
              </div>
            </article>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 mt-10">
            <button
              onClick={() =>
                setPage((p) => (p - 1 + totalPages) % totalPages)
              }
              className="w-10 h-10 rounded-full bg-white border border-slate-200 text-slate-700 hover:bg-orange-500 hover:text-white hover:border-orange-500 transition-colors flex items-center justify-center"
              aria-label="Previous"
            >
              <ChevronLeft size={18} />
            </button>

            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={`page-${i}`}
                onClick={() => setPage(i)}
                className={`w-9 h-9 font-semibold transition-colors ${
                  i === page
                    ? "bg-green-600 text-white"
                    : "bg-white border border-slate-200 text-slate-700 hover:border-orange-500 hover:text-orange-500"
                }`}
              >
                {i + 1}
              </button>
            ))}

            <button
              onClick={() => setPage((p) => (p + 1) % totalPages)}
              className="w-10 h-10 rounded-full bg-white border border-slate-200 text-slate-700 hover:bg-orange-500 hover:text-white hover:border-orange-500 transition-colors flex items-center justify-center"
              aria-label="Next"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default NewsBlog;